import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import SimpleActionButton from '../Modals/ActionButton/simpleActionButton';
import { FormInterface, FormGroup, FormTypeEnum } from './formInterfaces';
import EditInputForm from './FormTypes/editInputForm';
import MultiSelectListForm from './FormTypes/multiSelectListForm';
import NumberInputForm from './FormTypes/numberInputForm';
import RadioSelectForm from './FormTypes/radioSelectForm';
import SelectListForm from './FormTypes/selectListForm';
import TextareaForm from './FormTypes/textareaForm';
import RequiredFieldsNote from './RequiredFieldsNote';
import { StyledFormContainer, StyledFormControl, StyledFormGrid, StyledFormGridItem } from './styled';

const getFormGroupComponent = (formGroup: FormGroup) => {
    switch (formGroup.type) {
        case FormTypeEnum.TEXTAREA:
            return (
                <TextareaForm
                    key={formGroup.id}
                    information={formGroup.information}
                    isOptional={formGroup.isOptional}
                    label={formGroup.label}
                    onChange={formGroup.onChange}
                    placeholder={formGroup.placeholder}
                    value={formGroup.value}
                />
            );
        case FormTypeEnum.NUMBER:
            return (
                <NumberInputForm
                    key={formGroup.id}
                    information={formGroup.information}
                    isOptional={formGroup.isOptional}
                    label={formGroup.label}
                    onChange={formGroup.onChange}
                    placeholder={formGroup.placeholder}
                    value={formGroup.value}
                />
            );
        case FormTypeEnum.EDIT_INPUT:
            return (
                <EditInputForm
                    key={formGroup.id}
                    label={formGroup.label}
                    onChange={formGroup.onChange}
                    placeholder={formGroup.placeholder}
                    value={formGroup.value}
                />
            );
        case FormTypeEnum.RADIO_SELECT:
            return (
                <RadioSelectForm
                    key={formGroup.id}
                    choices={formGroup.choices}
                    information={formGroup.information}
                    isOptional={formGroup.isOptional}
                    label={formGroup.label}
                    onChange={formGroup.onChange}
                    placeholder={formGroup.placeholder}
                    value={formGroup.value}
                />
            );
        case FormTypeEnum.SELECT_LIST:
            return (
                <SelectListForm
                    key={formGroup.id}
                    choices={formGroup.choices}
                    disabled={formGroup.disabled}
                    information={formGroup.information}
                    isOptional={formGroup.isOptional}
                    label={formGroup.label}
                    onChange={formGroup.onChange}
                    placeholder={formGroup.placeholder}
                    value={formGroup.value}
                />
            );
        case FormTypeEnum.MULTI_SELECT_LIST:
            return (
                <MultiSelectListForm
                    key={formGroup.id}
                    choices={formGroup.choices}
                    information={formGroup.information}
                    isOptional={formGroup.isOptional}
                    label={formGroup.label}
                    onChange={formGroup.onChange}
                    placeholder={formGroup.placeholder}
                    value={formGroup.value}
                />
            );
        default:
            return null;
    }
};

const Form: FC<FormInterface> = ({ id, formGroups, handleNextStep }) => {
    const { t } = useTranslation('settings');
    const visibleGroups = formGroups.filter((fg) => fg.display);

    const fullWidthGroups = visibleGroups.filter((fg) => !fg.column);
    const columnedGroups = visibleGroups.filter((fg) => !!fg.column);
    const maxCol = Math.max(0, ...columnedGroups.map((fg) => fg.column as number));
    const columnBuckets: FormGroup[][] = Array.from(
        { length: maxCol },
        (_, i) => columnedGroups.filter((fg) => fg.column === i + 1),
    );

    const hasRequiredFields = visibleGroups.some((fg) => fg.isOptional === false);

    return (
        <StyledFormControl>
            <StyledFormContainer>
                {fullWidthGroups.map((fg) => getFormGroupComponent(fg))}
                {columnedGroups.length > 0 && (
                    <StyledFormGrid>
                        {columnBuckets.map((bucket, i) => (
                            <StyledFormGridItem key={`col-${i + 1}`}>
                                {bucket.map((fg) => getFormGroupComponent(fg))}
                            </StyledFormGridItem>
                        ))}
                    </StyledFormGrid>
                )}
            </StyledFormContainer>
            {hasRequiredFields && <RequiredFieldsNote />}
            {handleNextStep && (<SimpleActionButton handleClickConfirm={() => handleNextStep(id)} labelConfirm={t('form.nextStep')} />)}
        </StyledFormControl>
    );
};

export default Form;
